import React from 'react'
import PropTypes from 'prop-types'
import { Separator, DataBlock, Paragraph, FlowGrid } from '@fs/zion-ui'
import { getNestedSubelementsByType, useSlsStateContext } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import {
  getEventTypeFieldValue,
  getElementFieldValues,
  getRecordPrimaryEvent,
  sortPrimaryToTop,
} from '../utils/events.utils'

// Generic component for displaying event details (DATE, PLACE or RELATIONSHIP)
const EventDetailRow = ({ event, type, images, person }) => {
  const fieldValues = getElementFieldValues(event, type, images, person)

  if (fieldValues.length === 0) return null

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="pico">
      {fieldValues.map((item) => (
        <div key={`list-${type.toLowerCase()}-${item.id}`}>{`${item.displayLabel}: ${item.value}`}</div>
      ))}
    </FlowGrid>
  )
}

// Generic component for displaying all event details as rows.
const EventItem = ({ event, index, events, person }) => {
  const { t } = useTranslation()
  const { types: slsTypes, images } = useSlsStateContext()

  const eventType = getEventTypeFieldValue(event, slsTypes)
  const { primary } = event
  // Get field values to check if data exists
  const dateValues = getNestedSubelementsByType(event, 'DATE')
  const placeValues = getNestedSubelementsByType(event, 'PLACE')
  const relationshipValues = !!event?.relationshipData
  const primaryLabel = primary ? ` • ${t('primary-event', 'Primary Event')}` : ''
  const hasData = dateValues.length > 0 || placeValues.length > 0 || relationshipValues
  const isLastEvent = index === events.length - 1

  return (
    <>
      <DataBlock
        heading={`${eventType}${primaryLabel}`}
        data={
          hasData ? (
            <FlowGrid orientation="vertical" columnCount={1} gutters="pico">
              {dateValues.length > 0 && <EventDetailRow event={event} type="DATE" />}
              {placeValues.length > 0 && <EventDetailRow event={event} type="PLACE" />}
              {relationshipValues && (
                <EventDetailRow event={event} type="RELATIONSHIP" images={images} person={person} />
              )}
            </FlowGrid>
          ) : null
        }
      />
      {!isLastEvent && <Separator size="sm" />}
    </>
  )
}

/**
 * EventsReviewList
 *
 * Renders a read-only summary of events for review. Shows event dates and
 * places using nested subelements. When there are no events, displays a 'no events'
 * message if the card is reviewed, in edit mode, or showDataInPendingState is enabled.
 * Returns no content when in pending state without showDataInPendingState.
 *
 * @param {Object} props
 * @param {Array<Object>} [props.events] - Array of event objects. Each should include `id`, `label`, and `primary`.
 * @param {Object} props.person - The SLS person object.
 * @param {boolean} props.editMode - Whether the card is currently in edit mode.
 * @param {boolean} props.reviewed - Whether the card has been reviewed.
 * @param {boolean} props.showDataInPendingState - Whether to show event data when the card is in a pending state.
 * @returns {JSX.Element} Rendered list of event data blocks or an empty-state message.
 */
const EventsReviewList = ({ events = [], person, editMode, reviewed, showDataInPendingState }) => {
  const { t } = useTranslation()
  const { images } = useSlsStateContext()
  const recordPrimaryEvent = getRecordPrimaryEvent(person, images)

  if (!reviewed && !editMode && !showDataInPendingState) {
    // If in pending state, we don't want to show the "No events added" message, so return null instead of rendering the Paragraph component.
    return null
  }

  // Ensure the primary event is included in the display list, even if it's not in the provided events array
  const eventsForDisplay =
    recordPrimaryEvent && !events.some((e) => e?.id === recordPrimaryEvent?.id)
      ? [recordPrimaryEvent, ...events]
      : events
  const sortedEventsForDisplay = [...eventsForDisplay].sort(sortPrimaryToTop)

  return (
    <>
      {sortedEventsForDisplay.length > 0 ? (
        sortedEventsForDisplay.map((event, index) => (
          <EventItem key={event.id} event={event} index={index} events={sortedEventsForDisplay} person={person} />
        ))
      ) : (
        <Paragraph secondary size="sm">
          <i>{t('no-events-found', 'No events added')}</i>
        </Paragraph>
      )}
    </>
  )
}

EventsReviewList.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string,
      primary: PropTypes.bool,
    })
  ),
  editMode: PropTypes.bool,
  reviewed: PropTypes.bool,
  person: PropTypes.shape({
    id: PropTypes.string,
    recordId: PropTypes.string,
  }),
  showDataInPendingState: PropTypes.bool,
}

export default EventsReviewList
